import { mutate } from 'swr'
import { getFetcher, postFetcher, putFetcher } from 'api/swr'
import { getComponentCanvasSize } from 'Editor/utils/fullscreenType'
import { enqueueCptUploads, useCptUploadStore } from 'Editor/BottomPanel/BatchUpload/uploadQueue'
import { softDeleteLayer } from 'Editor/RightPanel/Layers/layerDelete'
import {
  DEFAULT_LAYER_COVER,
  decodeLayerCanvas,
  outputPlaceForPsd,
  psdPlaceError,
  type PsdFolderItem,
  type PsdOutputPlace,
  type PsdParseResult,
} from './parsePsd'

type CreateLayerRes = { lid?: string }

const createLayer = async (
  tid: string,
  folder: PsdFolderItem,
): Promise<{ lid: string; cover: string }> => {
  const cover = folder.icon?.code || folder.icon?.id || DEFAULT_LAYER_COVER
  const created = (await postFetcher('/layers', {
    arg: { tid, name: folder.name, cover },
  })) as CreateLayerRes
  const lid = created?.lid
  if (!lid) throw new Error(`创建层失败：${folder.name}`)

  const value: Record<string, unknown> = {
    name: folder.name,
    cover,
    zIndex: folder.zIndex,
    playIndex: folder.playIndex,
    maxChoose: folder.icon?.max ?? 1,
    optional: folder.icon?.opt ?? true,
  }
  if (folder.icon?.gid) value.gid = folder.icon.gid
  if (folder.icon?.body) value.bodymark = 1
  if (folder.icon?.water) value.watermark = 1

  await putFetcher(`/layers/${lid}`, {
    arg: { tid, key: 'form', value },
  })
  return { lid, cover }
}

export type ImportProgress = {
  label: string
  current: number
  total: number
}

const yieldUi = () => new Promise<void>((resolve) => {
  requestAnimationFrame(() => resolve())
})

const folderUploadItems = (
  tid: string,
  created: { lid: string; folder: PsdFolderItem },
  base: { w: number; h: number },
  place: PsdOutputPlace,
) => {
  const { lid, folder } = created
  return folder.images.map((image) => ({
    id: image.id,
    tid,
    lid,
    dlc: tid,
    fileName: image.name,
    original: '',
    preview: '',
    trim: false,
    ai: false,
    autoCover: image.fillsCanvas ? 0 : 1,
    directGcs: true,
    baseSize: base.w,
    baseHeight: base.h,
    decodeLayer: () => decodeLayerCanvas(image.layer),
    layerPlace: {
      left: image.left + place.offsetX,
      top: image.top + place.offsetY,
      canvas: place.canvas,
      canvasHeight: place.canvasHeight,
      clip: place.clip,
    },
  }))
}

export type ExistingLayer = {
  id?: string
  name?: string
  cover?: string
  z?: number
  zIndex?: number
}

export type PsdFolderPlan = {
  folder: PsdFolderItem
  /** create 新建层；append 组件进入同名已有层，或并入本次已规划的同名层 */
  action: 'create' | 'append'
  /** 已有层 id。并入本次新建层时要等那一层建完才有 id */
  lid?: string
  /** 这一组最终写入的层，在 folders 中的下标 */
  anchor: number
  layerName: string
  zIndex: number
}

const layerZ = (layer: ExistingLayer) =>
  Number(layer.zIndex ?? layer.z ?? 0) || 0

const dropExistingLayers = async (
  tid: string,
  layers: ExistingLayer[],
) => {
  const ids = layers
    .map((layer) => layer.id)
    .filter((id): id is string => Boolean(id) && id !== 'draft')
  if (ids.length === 0) return

  const { jobs, removeJob } = useCptUploadStore.getState()
  jobs.filter((job) => job.tid === tid).forEach((job) => removeJob(job.id))

  await Promise.all(ids.map((id) => softDeleteLayer(tid, id)))
  await mutate(['/layers', { tid }])
}

export const zOffsetForAppend = (
  existingLayers: ExistingLayer[] = [],
  replaceExisting = false,
) => {
  const live = existingLayers.filter(
    (layer) => layer.id && layer.id !== 'draft',
  )
  if (replaceExisting || live.length === 0) return 0
  return Math.max(0, ...live.map(layerZ)) + 1
}

const liveLayers = (layers: ExistingLayer[]) =>
  layers.filter((layer) => layer.id && layer.id !== 'draft')

const nameKey = (name?: string) => (name || '').trim()

const pickExistingLayerByName = (folder: PsdFolderItem, layers: ExistingLayer[]) => {
  const key = nameKey(folder.name)
  if (!key) return undefined
  const hits = layers.filter((layer) => nameKey(layer.name) === key)
  if (hits.length === 0) return undefined
  return [...hits].sort(
    (a, b) => layerZ(a) - layerZ(b) || (a.id || '').localeCompare(b.id || ''),
  )[0]
}

/** 追加导入时，组名和已有层名字相同就复用该层；对不上则新建。替换导入时全部新建 */
export const planPsdImport = (
  folders: PsdFolderItem[],
  existingLayers: ExistingLayer[] = [],
  replaceExisting = false,
): PsdFolderPlan[] => {
  const zOffset = zOffsetForAppend(existingLayers, replaceExisting)
  const pool = replaceExisting ? [] : liveLayers(existingLayers)
  const plans: PsdFolderPlan[] = []
  const claimed = new Map<string, number>()

  folders.forEach((folder, index) => {
    const key = replaceExisting ? '' : nameKey(folder.name)
    if (key) {
      const prev = claimed.get(key)
      if (prev != null) {
        const anchor = plans[prev]
        plans.push({
          folder,
          action: 'append',
          lid: anchor.lid,
          anchor: prev,
          layerName: anchor.layerName,
          zIndex: anchor.zIndex,
        })
        return
      }
      const existing = pickExistingLayerByName(folder, pool)
      if (existing?.id) {
        claimed.set(key, index)
        plans.push({
          folder,
          action: 'append',
          lid: existing.id,
          anchor: index,
          layerName: existing.name || folder.name,
          zIndex: layerZ(existing),
        })
        return
      }
      claimed.set(key, index)
    }
    plans.push({
      folder,
      action: 'create',
      anchor: index,
      layerName: folder.name,
      zIndex: folder.zIndex + zOffset,
    })
  })

  return plans
}

/** 弹窗里先建完全部层，建完后再一次性入队上传 */
export const importParsedPsd = async (
  tid: string,
  parsed: PsdParseResult,
  existingLayers: ExistingLayer[] = [],
  onLayerCreated?: (lid: string) => void,
  onProgress?: (info: ImportProgress) => void,
  replaceExisting = false,
): Promise<{ layerCount: number; reusedCount: number; componentCount: number }> => {
  const template = (await getFetcher(`/templates/${tid}`)) as {
    fullscreenType?: number
  }
  const place = outputPlaceForPsd(parsed.width, parsed.height)
  const placeError = psdPlaceError(place, template?.fullscreenType)
  if (!place || placeError) {
    throw new Error(placeError ?? 'PSD 画布尺寸不支持')
  }
  const base = getComponentCanvasSize(template?.fullscreenType)
  const plans = planPsdImport(parsed.folders, existingLayers, replaceExisting)
  const layerTotal = Math.max(plans.length, 1)
  if (replaceExisting) {
    onProgress?.({
      label: '正在删除现有层',
      current: 0,
      total: layerTotal,
    })
    await dropExistingLayers(tid, existingLayers)
    await yieldUi()
  }

  const anchorLids = new Map<number, string>()
  const targets: { lid: string; folder: PsdFolderItem }[] = []
  const createdLayers: { lid: string; cover: string }[] = []
  for (const [index, plan] of plans.entries()) {
    const reusing = Boolean(plan.lid || anchorLids.get(plan.anchor))
    onProgress?.({
      label: reusing
        ? `复用层 ${plan.layerName}`
        : `正在创建层 ${plan.folder.name}`,
      current: index,
      total: layerTotal,
    })
    await yieldUi()
    let lid = plan.lid || anchorLids.get(plan.anchor)
    if (!lid) {
      const created = await createLayer(tid, {
        ...plan.folder,
        zIndex: plan.zIndex,
      })
      lid = created.lid
      createdLayers.push(created)
    }
    anchorLids.set(plan.anchor, lid)
    onLayerCreated?.(lid)
    targets.push({ lid, folder: plan.folder })
  }
  if (createdLayers.length > 0) {
    onProgress?.({
      label: '正在绑定默认染色',
      current: layerTotal,
      total: layerTotal,
    })
    await yieldUi()
    await postFetcher(`/templates/${tid}/bind-default-tonings`, {
      arg: { layers: createdLayers },
    })
    await mutate(['/tonings', { tid }])
  }
  await mutate(['/layers', { tid }])

  const uploadItems = targets.flatMap((item) =>
    folderUploadItems(tid, item, base, place),
  )
  onProgress?.({
    label: '层已就绪，开始加入上传队列',
    current: layerTotal,
    total: Math.max(layerTotal, 1),
  })
  await yieldUi()
  await enqueueCptUploads(uploadItems)
  onProgress?.({
    label: '已全部入队，开始上传',
    current: layerTotal,
    total: Math.max(layerTotal, 1),
  })

  const reusedIds = new Set(
    plans.map((plan) => plan.lid).filter((lid): lid is string => Boolean(lid)),
  )
  return {
    layerCount: plans.filter((plan) => plan.action === 'create').length,
    reusedCount: reusedIds.size,
    componentCount: uploadItems.length,
  }
}
